import { Component, computed, effect, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SigningSessionEndReason } from '@app/models/signing-session/signing-session';
import { SigningSessionStateService } from '@app/services/signing-session-state.service';
import { ToastService } from '@app/services/toast.service';
import { SigningSessionUtil } from '@app/utilities/signing-session-util';
import { finalize } from 'rxjs/operators';

type OtpDialogStep = 'opening' | 'otp' | 'verified' | 'ended' | 'error';

const CODE_LENGTH = 6;

@Component({
  selector: 'app-signing-otp-dialog',
  templateUrl: './signing-otp-dialog.component.html',
  styleUrl: './signing-otp-dialog.component.scss',
})
export class SigningOtpDialogComponent {
  readonly state = inject(SigningSessionStateService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  readonly visible = signal(false);
  readonly step = signal<OtpDialogStep>('opening');
  readonly error = signal<string | null>(null);
  readonly verifying = signal(false);
  readonly confirmCloseOpen = signal(false);
  readonly closing = signal(false);
  readonly lostAccessOpen = signal(false);
  code = '';

  readonly windowLeft = computed(() => SigningSessionUtil.formatClock(this.state.otpSecondsLeft()));
  readonly attemptsRemaining = computed(() => this.state.session()?.otp.attemptsRemaining ?? 0);
  readonly windowClosed = computed(() => this.step() === 'otp' && this.state.otpSecondsLeft() === 0);

  readonly header = computed(() => {
    switch (this.step()) {
      case 'verified':
        return this.translate.instant('Signing_Otp_Header_Verified');
      case 'ended':
        return this.translate.instant('Signing_Otp_Header_Ended');
      case 'error':
        return this.translate.instant('Signing_Otp_Header_Error');
      default:
        return this.translate.instant('Signing_Otp_Header');
    }
  });

  readonly endedMessage = computed(() => {
    switch (this.state.lastEnded()?.endReason) {
      case SigningSessionEndReason.OtpAttemptsExhausted:
        return this.translate.instant('Signing_Otp_Ended_Attempts');
      case SigningSessionEndReason.IdleTimeout:
      case SigningSessionEndReason.HardCap:
        return this.translate.instant('Signing_Otp_Ended_Expired');
      default:
        return this.translate.instant('Signing_Otp_Ended_Generic');
    }
  });

  constructor() {
    effect(
      () => {
        if (this.step() === 'otp' && this.visible() && !this.state.session()) this.step.set('ended');
      },
      { allowSignalWrites: true },
    );
  }

  start(): void {
    this.code = '';
    this.error.set(null);
    this.step.set('opening');
    this.visible.set(true);

    this.state.openOrResume().subscribe({
      next: () => this.step.set('otp'),
      error: (error) => {
        this.error.set(this.state.messageFrom(error));
        this.step.set('error');
      },
    });
  }

  submit(): void {
    if (!this.canSubmit || this.verifying()) return;

    this.verifying.set(true);
    this.error.set(null);
    this.state
      .verify(this.code)
      .pipe(finalize(() => this.verifying.set(false)))
      .subscribe({
        next: () => {
          this.step.set('verified');
          setTimeout(() => this.visible.set(false), 900);
        },
        error: (error) => {
          this.code = '';
          if (!this.state.session()) {
            this.step.set('ended');
            return;
          }
          this.error.set(this.state.messageFrom(error));
        },
      });
  }

  /** Asks to confirm discarding the pending session. Also used by the dashboard's "Discard" link. */
  requestCloseSession(): void {
    this.confirmCloseOpen.set(true);
  }

  confirmCloseSession(): void {
    if (this.closing()) return;

    this.closing.set(true);
    this.state
      .close()
      .pipe(finalize(() => this.closing.set(false)))
      .subscribe({
        next: () => {
          this.confirmCloseOpen.set(false);
          this.visible.set(false);
          this.toast.success(this.translate.instant('Common_Success'), this.translate.instant('Signing_End_Success'));
        },
        error: (error) => {
          this.confirmCloseOpen.set(false);
          this.toast.error(this.translate.instant('Signing_Error_Title'), this.state.messageFrom(error));
        },
      });
  }

  openLostAccess(event: Event): void {
    event.preventDefault();
    this.lostAccessOpen.set(true);
  }

  startAgain(): void {
    this.start();
  }

  close(): void {
    this.visible.set(false);
  }

  get canSubmit(): boolean {
    return this.code.length === CODE_LENGTH && !this.windowClosed();
  }
}
